/**
 * NBPLCCarbonStrategy — Carbon Strategy page for NB
 * Route: /corporate/nbplc/carbon-strategy
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNBPLC } from "@/context/NBPLCContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { RefreshCw, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import client from "@/api/client";

const WHY_SELECTED = (o) => {
  const irr = o.irr_pct || 0; const pb = o.payback_years || 99; const cap = o.capex_usd || 0;
  if (irr >= 25 && pb <= 3) return { label: "Fast Payback", cls: "bg-teal-100 text-teal-700 border-teal-200" };
  if (irr >= 25) return { label: "High IRR", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  if (cap <= 2_000_000) return { label: "Low CAPEX", cls: "bg-blue-100 text-blue-700 border-blue-200" };
  if (o.reduction_tco2e_yr >= 5000) return { label: "High Impact", cls: "bg-purple-100 text-purple-700 border-purple-200" };
  return { label: "Strategic", cls: "bg-slate-100 text-slate-500 border-slate-200" };
};

// Opportunity classification flags (Quick Win + High Impact)
const OPP_FLAGS = (o) => {
  const flags = [];
  if ((o.irr_pct||0) >= 25 && (o.payback_years||99) <= 3) flags.push({ label:"Quick Win",   cls:"bg-teal-100 text-teal-700 border-teal-200" });
  if ((o.reduction_tco2e_yr||0) >= 5000)                   flags.push({ label:"High Impact", cls:"bg-purple-100 text-purple-700 border-purple-200" });
  return flags;
};

function OppCard({ opp, onTransition, transitioning }) {
  const [expanded, setExpanded] = useState(false);
  const ws = WHY_SELECTED(opp);
  const flags = OPP_FLAGS(opp);
  const canSend = opp.status === "promoted_to_strategy";
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-blue-100 text-blue-700 border-blue-200">In Strategy</span>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${ws.cls}`}>Why: {ws.label}</span>
              {flags.map(f => (
                <span key={f.label} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${f.cls}`}>{f.label}</span>
              ))}
            </div>
            <p className="text-sm font-bold text-slate-900">{opp.name}</p>
            <p className="text-xs text-slate-400">{(opp.bu_name||"").replace("NB ","")} · {(opp.opp_type||"").replace(/_/g," ")}</p>
          </div>
          <div className="text-center flex-shrink-0">
            <p className="text-2xl font-bold text-emerald-700">{opp.composite_score||0}</p>
            <p className="text-[9px] text-slate-400">Score</p>
          </div>
          <button onClick={() => setExpanded(v=>!v)} className="text-slate-400">
            {expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
          </button>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          {[["Reduction",`${Number(opp.reduction_tco2e_yr||0).toLocaleString()} tCO₂e/yr`,"text-emerald-700"],
            ["CAPEX",`$${((opp.capex_usd||0)/1e6).toFixed(1)}M`,""],
            ["IRR",`${opp.irr_pct||"—"}%`,"text-emerald-700"]].map(([l,v,c])=>(
            <div key={l} className="bg-slate-50 rounded-lg px-2 py-1.5">
              <p className="text-slate-400 text-[9px]">{l}</p>
              <p className={`font-bold ${c}`}>{v}</p>
            </div>
          ))}
        </div>
        {expanded && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[["Operational Value",`$${((opp.guaranteed_value_usd_yr||0)/1e6).toFixed(1)}M/yr`,"text-teal-700"],
                ["Payback",`${opp.payback_years||"—"}yr`,""],
                ["Pathway",(opp.pathway||"internal").toUpperCase(),""],
                ["VCM",opp.vcm_eligible?"Eligible":"No",""]].map(([l,v,c])=>(
                <div key={l}><p className="text-slate-400">{l}</p><p className={`font-semibold ${c}`}>{v}</p></div>
              ))}
            </div>
            <p className="text-[9px] text-slate-500 mt-2">{opp.description||""}</p>
          </div>
        )}
        {canSend && (
          <button onClick={() => onTransition(opp.opp_id, "structuring_in_progress")}
            disabled={transitioning}
            className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50">
            {transitioning ? <RefreshCw size={11} className="animate-spin"/> : <ArrowRight size={11}/>}
            Send to Structuring
          </button>
        )}
      </div>
    </div>
  );
}

export default function NBPLCCarbonStrategy() {
  const navigate = useNavigate();
  const { selectedPeriod, period, vbCache, vbLoading, invalidateVb } = useNBPLC();
  const [transitioning, setTrans] = useState(false);
  const [sort, setSort] = useState("composite_score");
  const [viewMode, setViewMode] = useState("strategy"); // "strategy" | "top5_all"

  const vbData = selectedPeriod ? (vbCache[selectedPeriod] || null) : null;
  const loading = vbLoading && !vbData;

  const handleTransition = async (opp_id, newStatus) => {
    setTrans(true);
    await client.post(`/nbplc/opportunities/${opp_id}/transition`, { status: newStatus });
    invalidateVb(selectedPeriod);
    setTrans(false);
  };

  const allOpps = vbData?.opportunities || [];
  const promoted = allOpps
    .filter(o => o.status === "promoted_to_strategy")
    .sort((a,b) => (b[sort]||0) - (a[sort]||0));
  const top5All  = allOpps
    .sort((a,b) => (b.composite_score||0) - (a.composite_score||0))
    .slice(0, 5);

  const displayed = viewMode === "strategy" ? promoted : top5All;

  if (loading) return <div className="p-6 space-y-4">{[1,2].map(i=><div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse"/>)}</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
          <PageHeader title="Carbon Strategy" />
          <p className="text-xs text-slate-400 mt-0.5">{period?.label} · Opportunities promoted from Value Bridge</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setViewMode("strategy")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${viewMode==="strategy"?"bg-[#0F172A] text-white border-transparent":"bg-white border-slate-200 text-slate-500"}`}>
            In Strategy ({promoted.length})
          </button>
          <button onClick={() => setViewMode("top5_all")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${viewMode==="top5_all"?"bg-[#0F172A] text-white border-transparent":"bg-white border-slate-200 text-slate-500"}`}>
            Top 5 by Score
          </button>
          <select value={sort} onChange={e=>setSort(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white">
            <option value="composite_score">Sort: Score</option>
            <option value="irr_pct">Sort: IRR</option>
            <option value="reduction_tco2e_yr">Sort: Reduction</option>
            <option value="capex_usd">Sort: CAPEX</option>
          </select>
          <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 font-bold">
            {promoted.length} in strategy
          </span>
        </div>
      </div>

      {displayed.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <p className="text-slate-500 text-sm">No opportunities in this view.</p>
          <button onClick={() => navigate("/corporate/nbplc/value-bridge")}
            className="mt-3 text-xs text-teal-600 underline">Promote opportunities from Value Bridge →</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {displayed.map(o => (
            <OppCard key={o.opp_id} opp={o} onTransition={handleTransition} transitioning={transitioning}/>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap text-xs">
        {[["← Value Bridge", "/corporate/nbplc/value-bridge"],
          ["Structuring →", "/corporate/nbplc/carbon-structuring"]].map(([l,p])=>(
          <button key={p} onClick={() => navigate(p)}
            className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-xl hover:border-teal-400 hover:text-teal-700 font-semibold">{l}</button>
        ))}
      </div>
    </div>
  );
}
